import * as React from 'react';
import { useIntl } from 'react-intl';
import CmdBox from '@commander-services/cmd-box'; // need fix
import { IFormSectionButton } from '../Forms/Section';
import RecipientForm from './RecipientForm';
import useAnalytics from '../../hooks/useAnalytics';

interface FormProps {
  recipientValues: any;
  addingAllowed?: boolean;
  reportId: string;
  handleRecipientValues: (values: Record<string, any>[] | null) => void;
}

export default function RecipientsBox(props: FormProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  const [isRecipientModalOpen, setIsRecipientModalOpen] = React.useState<boolean>(false);
  const [editingRecipient, setEditingRecipient] = React.useState<any>(null);
  const { trackEvent } = useAnalytics();

  const handleRecipientModal = (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (event) {
      event.preventDefault();
    }
    trackEvent(['auto reports', 'click', 'Autoreports recipient add']);
    setIsRecipientModalOpen(!isRecipientModalOpen);
    setEditingRecipient(null);
  };

  const handleRecipientModalEdit = (values: any) => {
    let recipient: any = [];
    if (props.recipientValues) {
      recipient = JSON.parse(JSON.stringify(props.recipientValues));
    }
    recipient[values.key] = values;
    props.handleRecipientValues(recipient);
    trackEvent(['auto reports', 'click', 'Autoreports recipient edit']);
  };

  const recipientSectionButtons: IFormSectionButton[] = [
    {
      title: f({ id: 'alarms.section.recipient.button.addRecipient' }),
      tooltip: f({ id: 'reports.section.recipient.button.addRecipient.tooltip' }),
      buttonCallback: handleRecipientModal,
    },
  ];

  const removeRecipient = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    const newRecipients: any = [];
    const id = event.currentTarget.getAttribute('data-key');
    props.recipientValues.forEach((item: any, key: number) => {
      if (String(id) !== String(key)) {
        newRecipients.push(item);
      }
    });
    trackEvent(['auto reports', 'click', 'Autoreports recipient remove']);
    props.handleRecipientValues(newRecipients);
  };

  const editRecipient = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (event) {
      event.preventDefault();
    }
    trackEvent(['auto reports', 'click', 'Autoreports recipient edit']);
    const id = event.currentTarget.getAttribute('data-key');
    props.recipientValues.forEach((item: any, key: number) => {
      if (String(id) === String(key)) {
        item.key = String(key);
        setEditingRecipient(item);
      }
    });
    setIsRecipientModalOpen(!isRecipientModalOpen);
  };

  const handleRecipientModalResponse = (values: any) => {
    let recipient: any = [];
    if (props.recipientValues) {
      recipient = JSON.parse(JSON.stringify(props.recipientValues));
    }
    recipient.push(values);
    props.handleRecipientValues(recipient);
    trackEvent(['auto reports', 'click', 'Autoreports recipient add']);
  };

  const resetRecipientModal = (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (event) {
      event.preventDefault();
    }
    trackEvent(['auto reports', 'click', 'Autoreports recipient close']);
    setIsRecipientModalOpen(false);
    setIsRecipientModalOpen(true);
  };

  return (
    <CmdBox
      id="auto-reports-recipient"
      title={f({ id: 'alarms.section.recipient' })}
      headerButtons={props.addingAllowed ? recipientSectionButtons : []}
    >
      {props.recipientValues && props.recipientValues.length === 0 && (
        <div style={{ color: '#ba0e48', marginBottom: '35px' }}>
          {f({ id: 'alarms.alert.bad.recipient.message' })}
        </div>
      )}
      {props.recipientValues && props.recipientValues.length > 0 && (
        <div className="form-group" style={{ marginTop: '-7px' }}>
          <div className="table-responsive height-auto">
            <table className="table table-striped table-bordered table-listings m-0">
              <thead>
                <tr>
                  <th>{f({ id: 'alarms.section.recipient' })}</th>
                  <th>{f({ id: 'alarms.section.recipient.email' })}</th>
                  <th className="text-center">{f({ id: 'waypoints.edit' })}</th>
                  <th className="text-center">{f({ id: 'table.deleteBtn' })}</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(props.recipientValues).map((item: any, key: number) => {
                  return (
                    <tr key={`table-tr-${key}`}>
                      <td data-cy={`autoReports-recipient-name-${key + 1}`}>
                        {item.fullname || item.name || ''}
                      </td>
                      <td data-cy={`autoReports-recipient-email-${key + 1}`}>{item.email}</td>
                      <td className="text-center">
                        <a
                          href="#"
                          title=""
                          className="cmd-icon cmd-icon-edit text-gray"
                          onClick={editRecipient}
                          data-key={key}
                          style={{ padding: '14px 45px', margin: '-14px -45px' }}
                          data-cy={`autoReports-recipient-edit-${key + 1}`}
                        >
                          <span className="sr-only">{f({ id: 'table.edit' })}</span>
                        </a>
                      </td>
                      <td className="text-center">
                        <a
                          href="#"
                          title=""
                          className="cmd-icon cmd-icon-bin text-gray"
                          onClick={removeRecipient}
                          data-key={key}
                          style={{ padding: '14px 45px', margin: '-14px -45px' }}
                          data-cy={`autoReports-recipient-remove-${key + 1}`}
                        >
                          <span className="sr-only">{f({ id: 'table.remove' })}</span>
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {isRecipientModalOpen && (
        <RecipientForm
          closeCallback={handleRecipientModal}
          openCallback={resetRecipientModal}
          responseCallback={handleRecipientModalResponse}
          editingRecipient={editingRecipient}
          editCallback={handleRecipientModalEdit}
          putRecipients={props.recipientValues || []}
          reportId={props.reportId}
        />
      )}
    </CmdBox>
  );
}
